import React, { useMemo, useState } from 'react';
import Select from './Select.jsx';
import Checkbox from './Checkbox.jsx';
import { useAppConfirm } from './AppDialog.jsx';
import { CHART_OF_ACCOUNTS, COA_GROUPS } from '../data.js';
import { resolveAccountVisibility, visibilityCounts, fmtMoney } from '../engine.js';

const TIER_FILTERS = [
  { id: 'primary',          label: 'Primary',           hint: 'High-signal accounts only — the default for executive scan.' },
  { id: 'primarySecondary', label: '+ Secondary',       hint: 'Add second-tier accounts with meaningful balances.' },
  { id: 'all',              label: 'All (incl. dust)',  hint: 'Surface every account on the chart — including tiny / inactive.' },
];

const TIER_BADGE = {
  primary:   { label: 'P', cls: 'bg-[rgba(74,222,128,0.18)] text-accent-green',   tip: 'Primary tier' },
  secondary: { label: 'S', cls: 'bg-[rgba(96,165,250,0.16)] text-accent-blue',    tip: 'Secondary tier' },
  dust:      { label: 'D', cls: 'bg-[rgba(140,148,167,0.16)] text-text-dim',      tip: 'Dust tier (hidden by default)' },
};

const HINT = 'mt-[0.7rem] text-[0.80rem] text-text-muted px-3 py-[0.55rem] bg-surface-el border-l-[3px] border-border-strong rounded-sm';
const PM_BTN = 'bg-transparent border border-border-strong text-text-muted px-[0.7rem] py-[0.4rem] rounded-sm cursor-pointer font-[inherit] text-[0.78rem] hover:text-text hover:border-accent-gold disabled:opacity-50 disabled:cursor-not-allowed';
const TAB_BTN_BASE = 'bg-transparent border-0 text-text-muted px-[9px] py-1 text-[0.72rem] font-[inherit] cursor-pointer border-r border-border last:border-r-0 hover:bg-surface-hover hover:text-text';

export default function AccountVisibilityPanel({ scenario, setScenario }) {
  const [search, setSearch] = useState('');
  const { requestConfirmation, dialog: confirmDialog } = useAppConfirm();
  const counts = useMemo(() => visibilityCounts(scenario), [scenario.accountVisibility, scenario.accountTierFilter]);

  const setTier = (tier) =>
    setScenario({ ...scenario, accountTierFilter: tier });

  const toggleAccount = (id, currentlyVisible) => {
    const next = { ...(scenario.accountVisibility ?? {}) };
    next[id] = !currentlyVisible;
    setScenario({ ...scenario, accountVisibility: next });
  };

  const resetAll = async () => {
    const ok = await requestConfirmation({
      title: 'Reset all per-account overrides?',
      message: 'The tier filter is preserved; only account-specific visibility overrides are cleared.',
      confirmLabel: 'Reset overrides',
      tone: 'danger',
    });
    if (!ok) return;
    setScenario((current) => ({ ...current, accountVisibility: {} }));
  };

  const groups = useMemo(() => {
    const q = search.trim().toLowerCase();
    const matchSearch = (a) => !q || a.label.toLowerCase().includes(q) || a.id.toLowerCase().includes(q);
    const grouped = {};
    for (const a of CHART_OF_ACCOUNTS) {
      if (!matchSearch(a)) continue;
      grouped[a.type] = grouped[a.type] || [];
      grouped[a.type].push(a);
    }
    return grouped;
  }, [search]);

  const overrideCount = Object.keys(scenario.accountVisibility ?? {}).length;

  return (
    <div className="flex flex-col gap-[0.6rem]">
      <div className="flex gap-2 items-center flex-wrap">
        <div className="inline-flex border border-border rounded-sm overflow-hidden" role="group" aria-label="Tier filter">
          {TIER_FILTERS.map((t) => {
            const active = scenario.accountTierFilter === t.id;
            const state = active ? 'bg-surface-el text-accent-gold font-semibold' : '';
            return (
              <button
                key={t.id}
                type="button"
                className={`${TAB_BTN_BASE} ${state}`}
                onClick={() => setTier(t.id)}
                title={t.hint}
              >
                {t.label}
              </button>
            );
          })}
        </div>
        <input
          type="search"
          className="flex-1 min-w-[180px] bg-surface-raised border border-border-strong text-text px-[0.6rem] py-[0.4rem] rounded-sm font-[inherit] text-[0.82rem]"
          placeholder="Filter by name or id…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search accounts"
        />
        <button
          type="button"
          className={PM_BTN}
          onClick={resetAll}
          disabled={overrideCount === 0}
          title="Clear all per-account overrides — return to tier-based defaults"
        >
          Reset overrides
        </button>
      </div>

      <p className={HINT}>
        {overrideCount > 0
          ? <><strong className="text-text">{overrideCount}</strong> per-account override{overrideCount === 1 ? '' : 's'} active. </>
          : 'No per-account overrides — pure tier-based defaults. '}
        Visibility persists across refreshes.
      </p>

      {/* v7.3 — Per-tab tier overrides */}
      <details className="mt-3 px-[0.7rem] py-[0.5rem] bg-surface-raised border border-dashed border-border rounded-sm">
        <summary className="cursor-pointer font-semibold text-[0.85rem] text-text">Per-tab tier overrides</summary>
        <p className={HINT}>
          Pin a specific tier for one tab. Leave "Use global" to follow the main
          filter above. Handy for "show all detail on Command but keep Budgets dense" workflows.
        </p>
        {['actuals', 'budgets', 'forecasts', 'fcc', 'synthesis'].map((tab) => {
          const current = scenario.perTabTierFilter?.[tab] ?? '';
          return (
            <div key={tab} className="flex items-center gap-2 my-[0.35rem]">
              <label className="min-w-[72px] font-semibold text-[0.75rem] uppercase tracking-[0.04em] text-accent-gold">{tab.toUpperCase()}</label>
              <Select
                className="flex-1 !flex"
                compact
                value={current}
                onChange={(e) => {
                  const val = e.target.value || undefined;
                  const next = { ...(scenario.perTabTierFilter ?? {}) };
                  if (val) next[tab] = val;
                  else delete next[tab];
                  setScenario({ ...scenario, perTabTierFilter: next });
                }}
              >
                <option value="">Use global</option>
                <option value="primary">Primary only</option>
                <option value="primarySecondary">Primary + Secondary</option>
                <option value="all">All (incl. dust)</option>
              </Select>
            </div>
          );
        })}
      </details>

      {/* Empty state when the search filter matches nothing across every group */}
      {search.trim() && Object.values(groups).every(list => !list || list.length === 0) && (
        <div className="text-center py-6 px-4 border border-dashed border-border rounded-sm text-text-dim italic text-[0.85rem]">
          No accounts match <strong className="text-text not-italic">"{search}"</strong>.
          <button
            type="button"
            className="ml-2 text-accent-blue hover:underline cursor-pointer bg-transparent border-0 font-[inherit] text-[inherit] not-italic"
            onClick={() => setSearch('')}
          >
            Clear search
          </button>
        </div>
      )}

      {Object.entries(COA_GROUPS).map(([type, label]) => {
        const accounts = groups[type] ?? [];
        if (accounts.length === 0) return null;
        const c = counts[type] ?? { visible: 0, total: 0 };
        return (
          <details key={type} className="border border-border rounded-sm bg-surface px-[0.7rem] py-[0.4rem]" open={type !== 'expense'}>
            <summary className="cursor-pointer flex justify-between items-center text-[0.85rem] py-[0.2rem]">
              <strong className="text-text">{label}</strong>
              <em className="text-text-muted not-italic text-[0.78rem]">{c.visible} visible of {c.total}</em>
            </summary>
            <ul className="list-none pt-[0.4rem] px-0 pb-0 m-0 flex flex-col gap-[0.15rem]">
              {accounts.map((a) => {
                const visible = resolveAccountVisibility(a, scenario);
                const t = TIER_BADGE[a.tier] ?? TIER_BADGE.secondary;
                const display = a.balance != null
                  ? fmtMoney(a.balance, { decimals: 2 })
                  : a.monthly != null
                    ? `${fmtMoney(a.monthly, { decimals: 2 })}/mo`
                    : '—';
                return (
                  <li key={a.id} className="grid grid-cols-[1fr_auto_auto] gap-2 items-center px-[0.3rem] py-1 border-b border-dashed border-border text-[0.82rem]">
                    <div className="flex items-center gap-2 min-w-0">
                      <Checkbox
                        checked={visible}
                        onChange={() => toggleAccount(a.id, visible)}
                      />
                      <span className={`overflow-hidden text-ellipsis whitespace-nowrap ${visible ? 'text-text' : 'text-text-dim line-through'}`}>{a.label}</span>
                    </div>
                    <span className={`inline-block text-[0.62rem] font-bold px-[5px] py-px rounded-[3px] uppercase tracking-[0.05em] ${t.cls}`} title={t.tip}>{t.label}</span>
                    <span className="text-text-muted tabular-nums text-[0.78rem]">{display}</span>
                  </li>
                );
              })}
            </ul>
          </details>
        );
      })}
      {confirmDialog}
    </div>
  );
}
